<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useGetters } from "vuex-composition-helpers";

import AtsLogo from "@/components/AtsLogo/AtsLogo";
import { lastSixDigits } from "@/common/misc/lastSixDigits";

const emit = defineEmits(["change", "search", "mine-only-change", "load-more"]);

defineExpose({
  clearSelection,
});

const props = defineProps({
  projectsCampaignsData: {
    type: Object,
    required: true,
    default: () => {
      return {
        projects: [],
        campaigns: [],
      }
    },
  },
  loading: {
    type: Boolean,
    default: false,
  },
  defaultProjectId: {
    type: Number,
    default: null,
  },
  defaultProjectCampaignId: {
    type: Number,
    default: null,
  },
  projectSelectionDisabled: {
    type: Boolean,
    default: false,
  },
  cacheEnabled: {
    type: Boolean,
    default: true,
  },
  disabledProjectIds: {
    type: Array,
    default: () => [],
  },
  showCampaigns: {
    type: Boolean,
    default: true,
  },
  showMineOnly: {
    type: Boolean,
    default: true,
  },
  showCampaignsOnlyForDefaultProject: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: "Choose a job or campaign",
  },
  mineOnly: {
    type: Boolean,
    default: true,
  },
  hasMore: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: "default",
    validator: function (value) {
      return ["mini", "small", "default", "large"].includes(value);
    },
  },
});

const {
  isUserSudoing,
  isCampaignFirstMessageGenerating,
  isCampaignFollowUpGenerating,
  isGeneratingNewCampaignForInAppSourcing,
} = useGetters([
  "isUserSudoing",
  "isCampaignFirstMessageGenerating",
  "isCampaignFollowUpGenerating",
  "isGeneratingNewCampaignForInAppSourcing",
]);

const mineOnly = ref(props.mineOnly);
const filterValue = ref("");
const selectedProjectCampaign = ref(null);
const selectRef = ref(null);
let dropdownScrollWrap = null;

const selectedProjectCampaignPrefixIcon = computed(() => {
  if (!selectedProjectCampaign.value) return "";

  if (selectedProjectCampaign.value.id === "save" || selectedProjectCampaign.value.campaigns) {
    return "briefcase";
  }

  return "envelope";
});

const isSelectorDisabled = computed(() => {
  return (
    isCampaignFollowUpGenerating.value ||
    isCampaignFirstMessageGenerating.value ||
    isGeneratingNewCampaignForInAppSourcing.value
  );
});

const allProjects = computed(() => {
  const campaignsPerProject = {};
  const { projects, campaigns } = props.projectsCampaignsData;

  campaigns.forEach(campaign => {
    if (campaignsPerProject[campaign.projectId]) {
      campaignsPerProject[campaign.projectId].push(campaign);
    } else {
      campaignsPerProject[campaign.projectId] = [campaign];
    }
  });

  // Mine-only filtering for projects is now handled server-side.
  // Only apply client-side campaign filtering when mine-only is toggled.
  return projects.map(project => ({
    campaigns: (campaignsPerProject[project.id] || [])
      .filter(c => !(props.showMineOnly && mineOnly.value) || c.isMine),
    ...project,
  }));
});

const allProjectsAndCampaigns = computed(() => {
  return props.projectsCampaignsData.campaigns.concat(allProjects.value);
});

const projectList = computed(() => {
  if (props.projectSelectionDisabled && props.defaultProjectId) {
    return allProjects.value.filter(item => item.id === props.defaultProjectId);
  } else {
    return allProjects.value;
  }
});

const hasNoDataWhenMineOnly = computed(() => {
  return !props.projectsCampaignsData.projects.length;
});

const mineOnlyTooltip = computed(() => {
  return hasNoDataWhenMineOnly.value ? "No jobs or campaigns created by me" : "Show only jobs and campaigns that I've created or sourced candidates to";
});

const displayedProjects = computed(() => {
  if (!filterValue.value) {
    return projectList.value;
  }

  // When there's a filter value, also filter campaigns client-side by name
  const filtered = [];
  projectList.value.forEach(project => {
    const matchingCampaigns = project.campaigns.filter(campaign =>
      campaign.name.toLowerCase().includes(filterValue.value.toLowerCase())
    );
    if (matchingCampaigns.length) {
      filtered.push({ ...project, campaigns: matchingCampaigns });
    } else {
      filtered.push(project);
    }
  });
  return filtered;
});

function isProjectDisabled(project) {
  return props.projectSelectionDisabled || props.disabledProjectIds.some(id => id === project.id);
}

function campaignTooltip(campaign) {
  if (campaign.isDisabled) {
    return `Can't select this campaign because you don't have permission to send on ${campaign.senderName}'s behalf.`;
  }
  return `${campaign.name} - Sender ${campaign.senderName}`;
}

function projectOrCampaignSelectionChanged() {
  if (props.cacheEnabled && !isUserSudoing.value && selectedProjectCampaign.value) {
    localStorage.setItem("previousSelectedProjectCampaign", JSON.stringify(selectedProjectCampaign.value));
  }
  emit("change", selectedProjectCampaign.value || null); // || null -- so it would pass null instead of '' when selection is cleared
}

function remoteSearch(query) {
  filterValue.value = query.trim();
  emit("search", filterValue.value);
}

function checkScrollPosition() {
  if (!dropdownScrollWrap) return;
  const { scrollTop, scrollHeight, clientHeight } = dropdownScrollWrap;
  if (scrollHeight - scrollTop - clientHeight < 200) {
    emit("load-more");
  }
}

function onDropdownScroll() {
  checkScrollPosition();
}

function attachDropdownScroll() {
  nextTick(() => {
    const popperEl = selectRef.value?.$refs?.popper?.$el;
    if (popperEl) {
      dropdownScrollWrap = popperEl.querySelector(".el-select-dropdown__wrap");
      if (dropdownScrollWrap) {
        dropdownScrollWrap.addEventListener("scroll", onDropdownScroll);
      }
    }
  });
}

function detachDropdownScroll() {
  if (dropdownScrollWrap) {
    dropdownScrollWrap.removeEventListener("scroll", onDropdownScroll);
    dropdownScrollWrap = null;
  }
}

function onDropdownVisibleChange(visible) {
  if (visible) {
    attachDropdownScroll();
  } else {
    detachDropdownScroll();
    filterValue.value = "";
    emit("search", "");
  }
}

function mineOnlyChangeHandler(newMineOnly) {
  if (props.cacheEnabled && !isUserSudoing.value) {
    localStorage.setItem("project-campaign-mine-only", newMineOnly);
  }
  if (newMineOnly && selectedProjectCampaign.value && !selectedProjectCampaign.value.isMine) {
    selectedProjectCampaign.value = null;
    projectOrCampaignSelectionChanged();
  }
  emit("mine-only-change", newMineOnly);
}

function clearSelection() {
  selectedProjectCampaign.value = null;
  projectOrCampaignSelectionChanged();
};

onBeforeUnmount(() => {
  detachDropdownScroll();
});

watch(() => props.projectsCampaignsData, () => {
  if (!props.loading && !props.projectSelectionDisabled && hasNoDataWhenMineOnly.value && mineOnly.value) {
    mineOnly.value = false;
    emit("mine-only-change", false);
  }
  // After new items are appended, re-check if we're still near the bottom
  nextTick(() => checkScrollPosition());
});

watch(allProjects, async (newProjects) => {
  if (props.defaultProjectId && !selectedProjectCampaign.value) {
    await nextTick();
    const found = newProjects.find(item => item.id === props.defaultProjectId);
    if (found) {
      selectedProjectCampaign.value = found;
      projectOrCampaignSelectionChanged();
    }
  }
});

watch(() => props.defaultProjectId, (newId) => {
  if (newId && newId !== selectedProjectCampaign.value?.id) {
    const found = allProjects.value.find(item => item.id === newId);
    if (found) {
      selectedProjectCampaign.value = found;
      projectOrCampaignSelectionChanged();
    }
  }
});


onMounted(() => {
  if (props.defaultProjectId) {
    selectedProjectCampaign.value = allProjects.value.find(item => item.id === props.defaultProjectId);
  } else if (props.enabledCache && !isUserSudoing.value && localStorage.getItem("previousSelectedProjectCampaign")) {
    selectedProjectCampaign.value = allProjectsAndCampaigns.value.find(item => JSON.stringify(item) === localStorage.getItem("previousSelectedProjectCampaign"));
  } else if (props.defaultProjectCampaignId) {
    selectedProjectCampaign.value = allProjectsAndCampaigns.value.find(item => item.id === props.defaultProjectCampaignId);
  }

  if (selectedProjectCampaign.value) {
    projectOrCampaignSelectionChanged();
  }
});

</script>

<template>
  <el-select
    ref="selectRef"
    v-model="selectedProjectCampaign"
    class="project-and-campaign-selector"
    :placeholder="placeholder"
    filterable
    remote
    :remote-method="remoteSearch"
    value-key="id"
    :loading="loading"
    :size="size"
    :disabled="isSelectorDisabled"
    no-data-text="No jobs or campaigns"
    @visible-change="onDropdownVisibleChange"
    @change="projectOrCampaignSelectionChanged"
  >
    <template v-if="selectedProjectCampaignPrefixIcon" #prefix>
      <span class="project-title">
        <AtsLogo v-if="selectedProjectCampaign.atsName" class="item-icon" :ats-name="selectedProjectCampaign.atsName" :problem="selectedProjectCampaign.atsJobProblem" />
        <img v-else class="item-icon" :src="require(`@/assets/images/icons/${selectedProjectCampaignPrefixIcon}.svg`)" />

        <span v-if="!selectedProjectCampaign.projectId" class="ats-logo-for-title d-flex align-items-center">
          <span v-if="selectedProjectCampaign.atsJobRequisitionId" class="ml-8">{{lastSixDigits(selectedProjectCampaign.atsJobRequisitionId)}}</span>
          <img v-if="selectedProjectCampaign.favorite" class="bookmark-icon ml-8" :src="require('@/assets/images/icons/bookmark.svg')" />
        </span>
      </span>
    </template>

    <slot></slot>

    <template v-if="showMineOnly">
      <el-tooltip
        :content="mineOnlyTooltip"
        :open-delay="500"
      >
        <div @click.stop @mousedown.stop>
          <el-switch
            v-model="mineOnly"
            class="pl-16"
            :disabled="hasNoDataWhenMineOnly"
            active-text="Mine only"
            @change="mineOnlyChangeHandler"
          />
        </div>
      </el-tooltip>

      <el-divider class="my-8" />
    </template>

    <template v-for="project in displayedProjects">
      <el-option
        v-if="!showCampaignsOnlyForDefaultProject"
        :key="project.id + '_project'"
        class="project"
        :class="{ 'only-projects': !showCampaigns }"
        :value="project"
        :disabled="isProjectDisabled(project)"
        :label="project.name"
      >
        <span class="d-flex align-items-center justify-content-between">
          <span class="d-flex align-items-center">
            <AtsLogo v-if="project.atsName" class="item-icon mr-8" :ats-name="project.atsName" :problem="project.atsJobProblem" />
            <img v-else class="item-icon mr-8" :src="require('@/assets/images/icons/briefcase.svg')" />
            <span class="project-name">{{ project.name }}</span>
          </span>

          <span class="d-flex align-items-center">
            <span v-if="project.atsJobRequisitionId" class="ml-8">{{lastSixDigits(project.atsJobRequisitionId)}}</span>
            <img v-if="project.favorite" class="bookmark-icon ml-8" :src="require('@/assets/images/icons/bookmark.svg')" />
          </span>
        </span>
      </el-option>

      <template v-if="showCampaigns">
        <el-option
          v-for="campaign in project.campaigns"
          :key="campaign.id + '_campaign'"
          :class="{ campaign: !showCampaignsOnlyForDefaultProject }"
          :value="campaign"
          :disabled="campaign.isDisabled"
          :label="campaign.name"
        >
          <el-tooltip :content="campaignTooltip(campaign)" :open-delay="500">
            <span class="d-flex align-items-center justify-content-between">
              <span class="d-flex align-items-center">
                <img
                  v-if="campaign.sourcingWorkflow === 'ops'" class="stream-icon"
                  :src="require('@/assets/images/icon-stream-blue.svg')"
                />
                <img class="item-icon mr-8" :src="require('@/assets/images/icons/envelope.svg')" />
                <span class="campaign-name ml-8">{{ campaign.name }}</span>
              </span>
              <span v-if="campaign.favorite" class="mr-8 material-icons-round md-18 rotate-45-right">push_pin</span>
            </span>
          </el-tooltip>
        </el-option>
      </template>
    </template>

    <el-option v-if="hasMore" key="__load_more__" :value="{ id: '__load_more__' }" disabled class="load-more-option">
      <div class="load-more-spinner">
        <i class="el-icon-loading" />
      </div>
    </el-option>
  </el-select>
</template>

<style lang="scss" scoped>
.project-and-campaign-selector {
  width: 100%;
}

::v-deep .el-input__prefix {
  width: 100%;
  display: flex;
  align-items: center;
  margin-right: 0;
}

.project-title {
  width: 100%;
  padding-right: 55px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.project {
  color: black;
  font-weight: 600;

  &.only-projects {
    font-weight: 400;
  }

  .project-name {
    max-width: 600px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .ats-logo {
    width: 24px;
    height: 24px;
  }
}

.bookmark-icon {
  width: 16px;
  height: 16px;
}

.campaign {
  padding-left: 40px;
  color: black;

  .campaign-name {
    max-width: 600px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .stream-icon {
    width: 18px;
    height: 18px;
  }
}

.load-more-option {
  text-align: center;
  cursor: default;

  .load-more-spinner {
    padding: 4px 0;
    color: #909399;
    font-size: 16px;
  }
}
</style>
